import { createFileRoute, Link } from "@tanstack/react-router";
import { PageShell, PageHero } from "@/components/site/PageShell";
import { seoMeta, canonicalLink, absoluteUrl, robotsMeta } from "@/lib/seo";
import { SITE, SITE_URL } from "@/lib/site";
import { DEFAULT_CONTACT, type ContactInfo } from "@/lib/site-contact";
import { getPublicContactInfo } from "@/server/site/contact.fns";

const DEFAULT_TITLE = "Nosotros · Agencia BTL en Lima | Improve Yourself";
const DEFAULT_DESCRIPTION =
  "Equipo BTL de marketing y operaciones en Lima. Trabajamos con atención directa, propuestas claras, presupuesto desglosado y ejecución supervisada.";

const principles = [
  {
    title: "Orden operativo",
    desc: "Cronogramas, responsables y reportes claros en cada proyecto: siempre sabes en qué etapa está el tuyo, sin tener que preguntar.",
  },
  {
    title: "Atención directa",
    desc: "Hablas con el equipo que ejecuta, no con un intermediario: las decisiones se toman rápido, sin teléfono malogrado entre tú y quien está en campo.",
  },
  {
    title: "Propuesta a medida",
    desc: "Cada cotización se arma según tu brief, no es una plantilla: no pagas por líneas que tu proyecto no necesita.",
  },
  {
    title: "Capacidad de ejecución",
    desc: "Producción, personal y supervisión coordinados por el mismo equipo: menos proveedores que gestionar tú mismo, menos riesgo de que algo falle entre ellos.",
  },
  {
    title: "Transparencia",
    desc: "Presupuesto desglosado por línea y reporte real de lo ejecutado: sabes en qué se va cada sol, antes y después de la campaña.",
  },
  {
    title: "Foco comercial",
    desc: "Cada activación está pensada para cumplir un objetivo medible: puedes justificar la inversión con resultados, no solo con fotos bonitas.",
  },
];

export const Route = createFileRoute("/nosotros")({
  component: Nosotros,
  loader: async (): Promise<{ contact: ContactInfo }> => {
    const contact = await getPublicContactInfo();
    return { contact };
  },
  head: ({ loaderData, match }) => {
    const contact = loaderData?.contact ?? DEFAULT_CONTACT;
    const override = match.context.seoOverride;
    const title = override?.metaTitle ?? DEFAULT_TITLE;
    const description = override?.metaDescription ?? DEFAULT_DESCRIPTION;
    return {
      meta: [
        ...seoMeta({
          title,
          description,
          path: "/nosotros",
          image: override?.ogImage ?? undefined,
        }),
        ...robotsMeta(override?.robotsIndex ?? true, override?.robotsFollow ?? true),
      ],
      links: canonicalLink(override?.canonicalPath ?? "/nosotros"),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Inicio", item: `${SITE_URL}/` },
              { "@type": "ListItem", position: 2, name: "Nosotros", item: `${SITE_URL}/nosotros` },
            ],
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            "@id": absoluteUrl("/nosotros"),
            url: absoluteUrl("/nosotros"),
            name: "Nosotros · Improve Yourself",
            inLanguage: "es-PE",
            description:
              "Equipo de marketing, producción y operaciones que opera como agencia BTL en Lima.",
            about: {
              "@type": "Organization",
              "@id": `${SITE_URL}/#organization`,
              name: SITE.name,
              url: SITE_URL,
              email: contact.email,
              telephone: contact.phone,
              description: SITE.description,
              foundingDate: "2018",
              areaServed: { "@type": "City", name: "Lima" },
              address: {
                "@type": "PostalAddress",
                addressLocality: "Lima",
                addressCountry: "PE",
              },
              knowsAbout: [
                "Activaciones BTL",
                "Eventos corporativos",
                "Trade marketing",
                "Sampling y degustaciones",
                "Impulsadoras y promotoras",
                "Volanteo publicitario",
                "Merchandising corporativo",
                "Stands para ferias",
              ],
            },
          }),
        },
      ],
    };
  },
});

function Nosotros() {
  return (
    <PageShell>
      <PageHero
        eyebrow="· Nosotros"
        h1="El equipo detrás de una agencia BTL en Lima"
        intro="Somos un equipo de marketing, producción y operaciones que produce activaciones BTL y eventos en Lima desde 2018, con experiencia directa dentro de otras empresas antes de armar Improve Yourself para ofrecer ese mismo nivel de ejecución como agencia propia."
      >
        <Link
          to="/cotizar"
          className="rounded-full bg-primary px-7 py-3.5 text-base font-semibold text-primary-foreground shadow-pop ring-4 ring-primary/15"
        >
          Solicitar propuesta →
        </Link>
      </PageHero>

      <section className="py-16">
        <div className="mx-auto max-w-3xl px-5 text-lg text-secondary/80 md:px-8">
          <h2 className="font-display text-3xl font-bold text-secondary md:text-4xl">
            Qué hacemos diferente
          </h2>
          <p className="mt-5">
            Activaciones, eventos, sampling y trade marketing — no como cliente interno, sino como
            el equipo que lo ejecutaba. Conocemos la diferencia entre una propuesta que se ve bien
            en papel y una operación que funciona en sitio.
          </p>
          <p className="mt-4">
            Trabajamos con propuestas claras, presupuestos desglosados y cronogramas reales. No
            vendemos promesas: vendemos una operación ordenada, supervisada y reportada.
          </p>
        </div>
      </section>

      <section className="bg-surface py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <h2 className="font-display text-3xl font-bold text-secondary md:text-4xl">
            Cómo trabajamos
          </h2>
          <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {principles.map((p) => (
              <div
                key={p.title}
                className="rounded-3xl border border-border bg-background p-6 shadow-soft"
              >
                <h3 className="font-display text-lg font-bold text-secondary">{p.title}</h3>
                <p className="mt-2 text-sm text-secondary/70">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 text-center">
        <div className="mx-auto max-w-3xl px-5 md:px-8">
          <h2 className="font-display text-3xl font-bold text-secondary md:text-4xl">
            ¿Tienes un proyecto en mente?
          </h2>
          <p className="mt-4 text-lg text-secondary/75">
            Cuéntanos el objetivo y las fechas. Te enviamos una propuesta clara con presupuesto
            desglosado en menos de 72 horas.
          </p>
          <Link
            to="/cotizar"
            className="mt-7 inline-flex rounded-full bg-primary px-8 py-4 text-base font-semibold text-primary-foreground shadow-pop ring-4 ring-primary/15"
          >
            Solicitar propuesta →
          </Link>
        </div>
      </section>
    </PageShell>
  );
}
